import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { UpdateInspectionInput, VehicleDetail } from 'used-vehicle-contracts';
import { orpc } from '../../../apis/orpc-query';
import { StatCard } from '../../../components/ui/DefinitionList';
import { Spinner } from '../../../components/ui/Spinner';
import { formatMoney, formatPercent } from '../../../lib/format';
import { serverMessage, submitErrorKey } from '../../../lib/form-helpers';
import { useI18nTranslation } from '../../../lib/use-i18n-translation';
import { InspectionFields, type InspectionFieldValues, type InspectionPatchValues } from '../../inspections/InspectionFields';

export interface VehicleInspectionTabProps {
  vehicle: VehicleDetail;
  editable: boolean;
  onChanged: () => Promise<void>;
}

const UNRECORDED: InspectionFieldValues = {
  grade7: 'UNKNOWN',
  interior: 'UNKNOWN',
  engine: 'UNKNOWN',
  hybrid: 'UNKNOWN',
  tires: 'UNKNOWN',
  damageGroup1Index: '',
  damageGroup2Index: '',
  inspectorNotes: '',
};

const fromVehicle = (vehicle: VehicleDetail): InspectionFieldValues =>
  vehicle.inspection === null
    ? { ...UNRECORDED }
    : {
        grade7: vehicle.inspection.grade7,
        interior: vehicle.inspection.interior,
        engine: vehicle.inspection.engine,
        hybrid: vehicle.inspection.hybrid,
        tires: vehicle.inspection.tires,
        damageGroup1Index: vehicle.inspection.damageGroup1Index === null ? '' : String(vehicle.inspection.damageGroup1Index),
        damageGroup2Index: vehicle.inspection.damageGroup2Index === null ? '' : String(vehicle.inspection.damageGroup2Index),
        inspectorNotes: vehicle.inspection.inspectorNotes ?? '',
      };

/**
 * Inspection — the estimator's step 2, with `owner = VEHICLE` (plan §7).
 *
 * The purchase copied the estimate's row onto the vehicle, so this tab opens with the condition the
 * price was based on rather than an empty form. Re-assessing a car that has been standing for six weeks
 * is a real thing to do, and every change here moves the required retail on the sticky card beside it.
 */
export const VehicleInspectionTab = ({ vehicle, editable, onChanged }: VehicleInspectionTabProps) => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();

  const [values, setValues] = useState<InspectionFieldValues>(() => fromVehicle(vehicle));
  const [error, setError] = useState<string | null>(null);

  const catalogQuery = useQuery(orpc.templates.paramCatalog.queryOptions());
  const templateVersionQuery = useQuery(
    orpc.templates.getVersion.queryOptions({
      input: { versionId: vehicle.template?.versionId ?? '' },
      enabled: Boolean(vehicle.template?.versionId),
    }),
  );
  const computeQuery = useQuery(orpc.valuations.compute.queryOptions({ input: { owner: { type: 'VEHICLE', id: vehicle.id } } }));
  const update = useMutation(orpc.inspections.update.mutationOptions());

  useEffect(() => {
    setValues(fromVehicle(vehicle));
  }, [vehicle]);

  const save = async (patch: InspectionPatchValues) => {
    setError(null);

    try {
      await update.mutateAsync({ owner: { type: 'VEHICLE', id: vehicle.id }, ...patch } as UpdateInspectionInput);
      await queryClient.invalidateQueries({ queryKey: orpc.valuations.compute.key() });
      await onChanged();
    } catch (caught: unknown) {
      setError(serverMessage(caught) ?? t(submitErrorKey(caught)));
    }
  };

  const outputs = computeQuery.data?.outputs ?? {};
  const missing = computeQuery.data?.missing ?? [];

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
      <div className="space-y-4">
        {error === null ? null : (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </p>
        )}

        <InspectionFields
          values={values}
          fuel={vehicle.fuel}
          damageOptions={catalogQuery.data ?? null}
          templateValues={templateVersionQuery.data ?? null}
          disabled={!editable || update.isPending}
          onChange={(next, patch) => {
            setValues((current) => ({ ...current, ...next }));
            void save(patch);
          }}
        />
      </div>

      <aside className="h-fit space-y-3 lg:sticky lg:top-6">
        <StatCard
          label={t('vehicles.inspectionImpact')}
          value={
            computeQuery.isLoading ? (
              <Spinner />
            ) : (
              <span
                dir="ltr"
                className="tabular-nums"
              >
                {formatPercent(outputs.inspection ?? null)}
              </span>
            )
          }
          hint={t('vehicles.inspectionImpactHint')}
        />
        <StatCard
          label={t('vehicles.fixedDamage')}
          value={
            <span
              dir="ltr"
              className="tabular-nums"
            >
              {formatMoney(outputs.fixed ?? null, { exact: true })}
            </span>
          }
          hint={t('vehicles.fixedDamageHint')}
        />
        <StatCard
          label={t('estimates.requiredRetail')}
          value={
            <span
              dir="ltr"
              className="tabular-nums"
            >
              {formatMoney(outputs.retail ?? null)}
            </span>
          }
          hint={t('vehicles.requiredRetailHint')}
        />
        {missing.length > 0 ? (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('estimates.missingTitle')}</p>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              {missing.map((entry) => (
                <li key={entry.name}>{t(`estimates.fields.${entry.field}`, { defaultValue: entry.field })}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </aside>
    </div>
  );
};
